import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as ClockIcon } from '@blink/assets/src/icons/clock.svg';

import { useManageStyle } from './Manage.style';
import { ManageItem } from './ManageItem';

import { EmptyBanner } from '@/widgets/Banners/EmptyBanner';
import { Store } from '@/shared/store';
import { ROLE } from '@/shared/api/vault';
import { ServerError } from '@/widgets/ServerError';
import { useHasVaultsError } from '@/shared/hooks/useErrors';
import { getVaultsAction } from '@/shared/store/vaults/actions';
import { Button, ButtonVariant } from '@/shared/ui';
import { useOpenConnectWalletModal } from '@/shared/hooks/useOpenConnectWalletModal';

export const Manage: FC = () => {
    const classes = useManageStyle();

    const { vaults, walletAddress } = useSelector((state: Store) => ({
        walletAddress: state.wallets.active.address,
        vaults: state.vaults.vaults,
    }));

    const hasError = useHasVaultsError();

    const manageVaults = useMemo(
        () => vaults.filter((vault) => vault.role === ROLE.CURATOR),
        [vaults],
    );

    const handleConnectWalletClick = useOpenConnectWalletModal();

    return (
        <div>
            <div className={classes.header}>
                <div className={classes.heading}>
                    <div className={classes.title}>
                        <div className={classes.titleText}>My Vaults</div>
                        <div className={classes.description}>
                            You’ve been granted the role of Asset Manager for the following vaults.
                        </div>
                    </div>
                </div>
            </div>

            {hasError ? (
                <ServerError action={getVaultsAction} />
            ) : !walletAddress ? (
                <div className='px-[10%] mt-[52px]'>
                    <EmptyBanner
                        customTitle='You need to connect a wallet.'
                        customSubtitle=' '
                        icon={ClockIcon}
                        component={
                            <Button
                                variant={ButtonVariant.Primary}
                                onClick={handleConnectWalletClick}
                                name='connect-wallet'
                                className='flex justify-center items-center w-[152px] text-md h-[44px]'
                            >
                                Connect Wallet
                            </Button>
                        }
                    />
                </div>
            ) : manageVaults.length === 0 ? (
                <div className='px-[10%] mt-[52px]'>
                    <EmptyBanner
                        customTitle='You don’t have any vaults yet.'
                        customSubtitle='Please check back later or contact support if you need help with vaults.'
                        icon={ClockIcon}
                    />
                </div>
            ) : (
                <div className={classes.content}>
                    {manageVaults.map((vault, idx) => (
                        <ManageItem key={idx} vault={vault} />
                    ))}
                </div>
            )}
        </div>
    );
};
